import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

class DongchediAPIClient {
  constructor() {
    this.apiUrl = 'https://chatkb.gausscode.com/api/v1/chat/completions';
    this.apiKey = 'fastgpt-hTCTb7Bi1PJt2hnx0CeDEVLNR7TmYIcoowfSaOG4xDlXrH9tBrDs6bHTJkf36';
    this.appId = '68ea0c653aa3a9faaf511a91';
  }

  // 执行SQL查询
  async executeQuery(sql) {
    const requestBody = {
      chatId: uuidv4(),
      stream: false,
      detail: false,
      responseChatItemId: uuidv4(),
      variables: {
        sql: sql
      },
      messages: [
        {
          role: "user",
          content: "执行SQL查询"
        }
      ]
    };

    try {
      const response = await axios.post(this.apiUrl, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        }
      });

      const data = response.data;
      
      if (data.choices && data.choices[0] && data.choices[0].message) {
        const content = data.choices[0].message.content;
        try {
          // 兼容Markdown代码块包裹
          let trimmed = content.trim();
          if (trimmed.startsWith('```')) {
            trimmed = trimmed.replace(/^```[a-zA-Z]*\n?/, '').replace(/```$/, '').trim();
          }
          
          const parsed = JSON.parse(trimmed);
          
          // 确保返回格式统一为 { result: [...] }
          // 如果直接是数组，包装成标准格式
          if (Array.isArray(parsed)) {
            return { result: parsed };
          }
          
          // 如果已经是对象格式，直接返回
          return parsed;
        } catch (e) {
          console.error('解析响应内容失败:', content);
          throw new Error('响应格式错误: ' + e.message);
        }
      } else {
        throw new Error('API响应格式错误');
      }
    } catch (error) {
      console.error('API请求失败:', error);
      throw error;
    }
  }

  // 获取口碑数据统计
  async getSentimentStats(filters = {}) {
    const whereConditions = this.buildWhereConditions(filters);
    const sql = `
      SELECT 
        COUNT(*) as total_count,
        COUNT(CASE WHEN s.sentiment = 'positive' THEN 1 END) as positive_count,
        COUNT(CASE WHEN s.sentiment = 'negative' THEN 1 END) as negative_count,
        COUNT(CASE WHEN s.sentiment = 'neutral' THEN 1 END) as neutral_count,
        AVG(CAST(s.sentiment_score AS DECIMAL(10,2))) as avg_score
      FROM dongchedi_sentiment_analysis s
      LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
      ${whereConditions}
    `;

    const result = await this.executeQuery(sql);
    return (result.result && result.result[0]) ? result.result[0] : {};
  }

  // 获取情感分布数据
  async getSentimentDistribution(filters = {}) {
    const whereConditions = this.buildWhereConditions(filters);
    const sql = `
      SELECT 
        s.sentiment,
        COUNT(*) as count,
        ROUND(AVG(CAST(s.sentiment_score AS DECIMAL(10,2))), 2) as avg_score
      FROM dongchedi_sentiment_analysis s
      LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
      ${whereConditions}
      GROUP BY s.sentiment
      ORDER BY count DESC
    `;

    const result = await this.executeQuery(sql);
    return result.result || [];
  }

  // 获取维度分析数据
  async getAspectAnalysis(filters = {}) {
    const whereConditions = this.buildWhereConditions(filters);
    const sql = `
      SELECT 
        s.original_sentence,
        s.sentiment,
        s.sentiment_score,
        s.opinion_analysis,
        r.car_name,
        r.series_name,
        s.create_time
      FROM dongchedi_sentiment_analysis s
      LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
      ${whereConditions}
      ORDER BY s.create_time DESC
      LIMIT 100
    `;

    const result = await this.executeQuery(sql);
    return result.result || [];
  }

  // 话题词云（基于 opinion_analysis 的第一条 aspect 提取）
  async getWordCloudAspects(filters = {}) {
    const whereConditions = this.buildWhereConditions(filters);
    const sql = `
      SELECT 
        JSON_UNQUOTE(JSON_EXTRACT(s.opinion_analysis, '$[0].aspect')) AS keyword,
        COUNT(*) AS count
      FROM dongchedi_sentiment_analysis s
      LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
      ${whereConditions}
      AND s.opinion_analysis IS NOT NULL
      AND s.opinion_analysis != ''
      AND JSON_VALID(s.opinion_analysis)
      GROUP BY keyword
      HAVING keyword IS NOT NULL AND keyword != ''
      ORDER BY count DESC
      LIMIT 50
    `;
    const result = await this.executeQuery(sql);
    return result.result || [];
  }

  // 话题情绪分布（Top10 aspect × 情绪分布）
  async getTopicSentimentDistribution(filters = {}) {
    const whereOuter = this.buildWhereConditions(filters);
    const whereSub = whereOuter.replaceAll('s.', 's2.').replaceAll('r.', 'r2.');
    const sql = `
      SELECT 
        t.keyword AS aspect,
        s.sentiment,
        COUNT(*) AS count
      FROM dongchedi_sentiment_analysis s
      LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
      JOIN (
        SELECT 
          JSON_UNQUOTE(JSON_EXTRACT(s2.opinion_analysis, '$[0].aspect')) AS keyword,
          COUNT(*) AS cnt
        FROM dongchedi_sentiment_analysis s2
        LEFT JOIN dongchedi_review_data r2 ON s2.source_id = r2.id
        ${whereSub}
        AND s2.opinion_analysis IS NOT NULL
        AND s2.opinion_analysis != ''
        AND JSON_VALID(s2.opinion_analysis)
        GROUP BY keyword
        HAVING keyword IS NOT NULL AND keyword != ''
        ORDER BY cnt DESC
        LIMIT 10
      ) t ON JSON_UNQUOTE(JSON_EXTRACT(s.opinion_analysis, '$[0].aspect')) = t.keyword
      ${whereOuter}
      GROUP BY t.keyword, s.sentiment
      ORDER BY t.keyword
    `;
    const result = await this.executeQuery(sql);
    return result.result || [];
  }

  async getSentimentWords() {
    const sql = `
      SELECT 
        s.sentiment_word AS word,
        s.sentiment_type AS sentiment,
        COUNT(*) AS count
      FROM dongchedi_sentiment_analysis s
      GROUP BY s.sentiment_word, s.sentiment_type
      HAVING s.sentiment_word IS NOT NULL AND s.sentiment_word != ''
      ORDER BY count DESC
      LIMIT 200
    `;
    const result = await this.executeQuery(sql);
    return result.result || [];
  }

  // 构建WHERE条件（时间范围统一使用 r.create_time）
  buildWhereConditions(filters) {
    const conditions = [];
    
    // 必须条件：情感倾向不能为空
    conditions.push(`s.sentiment IS NOT NULL`);
    conditions.push(`s.sentiment != ''`);
    
    // 口碑表条件
    if (filters.sentiment && filters.sentiment !== 'all') {
      conditions.push(`s.sentiment = '${filters.sentiment}'`);
    }
    if (filters.textType && filters.textType !== 'all') {
      conditions.push(`s.text_type = '${filters.textType}'`);
    }
    if (filters.complexity && filters.complexity !== 'all') {
      conditions.push(`s.sentence_complexity = '${filters.complexity}'`);
    }
    
    // 原始数据表条件
    if (filters.carReviewType && filters.carReviewType !== 'all') {
      conditions.push(`r.car_review_type = '${filters.carReviewType}'`);
    }
    if (filters.fuelForm && filters.fuelForm !== 'all') {
      conditions.push(`r.fuel_form = '${filters.fuelForm}'`);
    }
    if (filters.year && filters.year !== 'all') {
      conditions.push(`r.year = '${filters.year}'`);
    }
    if (filters.seriesName && filters.seriesName !== 'all') {
      conditions.push(`r.series_name = '${filters.seriesName}'`);
    }
    if (filters.carName && filters.carName !== 'all') {
      conditions.push(`r.car_name = '${filters.carName}'`);
    }
    
    // 时间范围（统一使用原始数据表的 create_time）
    if (filters.startDate) {
      conditions.push(`r.create_time >= '${filters.startDate}'`);
    }
    if (filters.endDate) {
      conditions.push(`r.create_time <= '${filters.endDate}'`);
    }
    
    // 关键词筛选（匹配 opinion_analysis 中的 aspect）
    if (filters.keyword && filters.keyword !== 'all') {
      conditions.push(`JSON_UNQUOTE(JSON_EXTRACT(s.opinion_analysis, '$[0].aspect')) = '${filters.keyword}'`);
    }
    
    return 'WHERE ' + conditions.join(' AND ');
  }

  // 基于现有filters构建WHERE，但排除exceptKeys中的维度（用于facet统计）
  buildWhereConditionsExcept(filters = {}, exceptKeys = []) {
    const f = { ...(filters || {}) };
    const omit = new Set(exceptKeys || []);
    const conditions = [];
    // 必须：情感非空（只有在不忽略sentiment时才加此条件）
    if (!omit.has('sentiment')) {
      conditions.push(`s.sentiment IS NOT NULL`);
      conditions.push(`s.sentiment != ''`);
    }
    // 口碑表
    if (!omit.has('sentiment') && f.sentiment && f.sentiment !== 'all') conditions.push(`s.sentiment = '${f.sentiment}'`);
    if (!omit.has('textType') && f.textType && f.textType !== 'all') conditions.push(`s.text_type = '${f.textType}'`);
    if (!omit.has('complexity') && f.complexity && f.complexity !== 'all') conditions.push(`s.sentence_complexity = '${f.complexity}'`);
    // 原始表
    if (!omit.has('carReviewType') && f.carReviewType && f.carReviewType !== 'all') conditions.push(`r.car_review_type = '${f.carReviewType}'`);
    if (!omit.has('fuelForm') && f.fuelForm && f.fuelForm !== 'all') conditions.push(`r.fuel_form = '${f.fuelForm}'`);
    if (!omit.has('year') && f.year && f.year !== 'all') conditions.push(`r.year = '${f.year}'`);
    if (!omit.has('seriesName') && f.seriesName && f.seriesName !== 'all') conditions.push(`r.series_name = '${f.seriesName}'`);
    if (!omit.has('carName') && f.carName && f.carName !== 'all') conditions.push(`r.car_name = '${f.carName}'`);
    // 时间
    if (f.startDate) conditions.push(`r.create_time >= '${f.startDate}'`);
    if (f.endDate) conditions.push(`r.create_time <= '${f.endDate}'`);
    // 关键词
    if (!omit.has('keyword') && f.keyword && f.keyword !== 'all') {
      conditions.push(`JSON_UNQUOTE(JSON_EXTRACT(s.opinion_analysis, '$[0].aspect')) = '${f.keyword}'`);
    }
    // 如果没有任何条件，返回 'WHERE 1=1'，以便后续安全追加 AND 条件
    return conditions.length > 0 ? 'WHERE ' + conditions.join(' AND ') : 'WHERE 1=1';
  }

  // 返回各筛选维度的候选值（带数量），会过滤掉无数据项
  async getFilterFacets(filters = {}) {
    const facets = {};
    // 定义每个facet的SQL
    const facetSqls = {
      sentiments: () => `
        SELECT s.sentiment AS value, COUNT(*) AS count
        FROM dongchedi_sentiment_analysis s
        LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
        ${this.buildWhereConditionsExcept(filters, ['sentiment'])}
        AND s.sentiment IS NOT NULL AND s.sentiment != ''
        GROUP BY s.sentiment
        HAVING COUNT(*) > 0
        ORDER BY count DESC
      `,
      textTypes: () => `
        SELECT s.text_type AS value, COUNT(*) AS count
        FROM dongchedi_sentiment_analysis s
        LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
        ${this.buildWhereConditionsExcept(filters, ['textType'])}
        AND s.text_type IS NOT NULL AND s.text_type != ''
        GROUP BY s.text_type
        HAVING COUNT(*) > 0
        ORDER BY count DESC
      `,
      complexities: () => `
        SELECT s.sentence_complexity AS value, COUNT(*) AS count
        FROM dongchedi_sentiment_analysis s
        LEFT JOIN dongchedi_review_data r ON s.source_id = r.id
        ${this.buildWhereConditionsExcept(filters, ['complexity'])}
        AND s.sentence_complexity IS NOT NULL AND s.sentence_complexity != ''
        GROUP BY s.sentence_complexity
        HAVING COUNT(*) > 0
        ORDER BY count DESC
      `,
      carReviewTypes: () => `
        SELECT r.car_review_type AS value, COUNT(*) AS count
        FROM dongchedi_sentiment_analysis s
        INNER JOIN dongchedi_review_data r ON s.source_id = r.id
        ${this.buildWhereConditionsExcept(filters, ['carReviewType'])}
        AND r.car_review_type IS NOT NULL AND r.car_review_type != ''
        GROUP BY r.car_review_type
        HAVING COUNT(*) > 0
        ORDER BY count DESC
      `,
      fuelForms: () => `
        SELECT r.fuel_form AS value, COUNT(*) AS count
        FROM dongchedi_sentiment_analysis s
        INNER JOIN dongchedi_review_data r ON s.source_id = r.id
        ${this.buildWhereConditionsExcept(filters, ['fuelForm'])}
        AND r.fuel_form IS NOT NULL AND r.fuel_form != ''
        GROUP BY r.fuel_form
        HAVING COUNT(*) > 0
        ORDER BY count DESC
      `,
      years: () => `
        SELECT r.year AS value, COUNT(*) AS count
        FROM dongchedi_sentiment_analysis s
        INNER JOIN dongchedi_review_data r ON s.source_id = r.id
        ${this.buildWhereConditionsExcept(filters, ['year'])}
        AND r.year IS NOT NULL AND r.year != ''
        GROUP BY r.year
        HAVING COUNT(*) > 0
        ORDER BY value DESC
      `,
      seriesNames: () => {
        const sql = `
          SELECT r.series_name AS value, COUNT(*) AS count
          FROM dongchedi_sentiment_analysis s
          INNER JOIN dongchedi_review_data r ON s.source_id = r.id
          ${this.buildWhereConditionsExcept(filters, ['seriesName'])}
          AND r.series_name IS NOT NULL AND r.series_name != ''
          GROUP BY r.series_name
          HAVING COUNT(*) > 0
          ORDER BY count DESC
          LIMIT 200
        `;
        console.log('车系SQL:', sql);
        return sql;
      },
      carNames: () => {
        const sql = `
          SELECT r.car_name AS value, COUNT(*) AS count
          FROM dongchedi_sentiment_analysis s
          INNER JOIN dongchedi_review_data r ON s.source_id = r.id
          ${this.buildWhereConditionsExcept(filters, ['carName'])}
          AND r.car_name IS NOT NULL AND r.car_name != ''
          GROUP BY r.car_name
          HAVING COUNT(*) > 0
          ORDER BY count DESC
          LIMIT 300
        `;
        console.log('车型SQL:', sql);
        return sql;
      }
    };
    // 顺序执行，避免并发太多
    for (const [key, builder] of Object.entries(facetSqls)) {
      try {
        const result = await this.executeQuery(builder());
        // executeQuery 返回的格式应该是 { result: [...] }
        const rows = result.result || [];
        facets[key] = rows.filter(i => i && i.value !== null && i.value !== '');
        console.log(`[facet] ${key}:`, facets[key]?.length || 0);
      } catch (e) {
        console.error('获取facet失败:', key, e);
        facets[key] = [];
      }
    }
    return facets;
  }

  // 获取竞品数据
  async getCompetitorData(mode, selectedObjects = [], baseFilters = {}) {
    const results = [];
    
    for (const name of selectedObjects) {
      const filters = { ...(baseFilters || {}) };
      if (mode === 'series') {
        filters.seriesName = name;
      } else {
        filters.carName = name;
      }

      const [stats, sentimentDist] = await Promise.all([
        this.getSentimentStats(filters),
        this.getSentimentDistribution(filters)
      ]);

      results.push({
        name,
        stats: stats || {},
        sentimentDistribution: sentimentDist || []
      });
    }

    return results;
  }
}

export default new DongchediAPIClient();

